// Vues, filtres et indicateurs du plan d'action. Logique pure, utilisée par le front.

import { eur, eurSigned } from '../format.js';
import type {
  ActionKind,
  Affaire,
  AffaireInterne,
  CostCause,
  DateIssueKind,
  PlanDataset,
  SavingOnly,
} from './types.js';

export type PlanView = 'plan' | 'drift' | 'dates' | 'sav' | 'rec';
export type PrioFilter = 'ch' | 'crit' | 'all';
export type DriftMode = 'ratt' | 'past' | 'all';
export type RecMode = 'drv' | 'up' | 'dn' | 'ok' | 'all';
export type SavMode = 'all' | 'perte' | 'gain';

export type TrtFilter = 'todo' | 'done' | 'all';

export interface PlanFilters {
  /** Code entité SYNC, ou 'Tous'. */
  ent: string;
  /** Type d'affaire, '' pour tous, '-' pour non renseigné. */
  ty: string;
  view: PlanView;
  kinds: string[];
  prio: PrioFilter;
  who: string;
  q: string;
  /** Facturé ≤ fmax %. */
  fmax: number | null;
  /** CA ≥ camin k€. */
  camin: number | null;
  dmode: DriftMode;
  rmode: RecMode;
  smode: SavMode;
  /** Suivi « traité » : à traiter, traitées ou toutes. */
  trt: TrtFilter;
}

export const DEFAULT_FILTERS: PlanFilters = {
  ent: 'Tous',
  ty: '',
  view: 'plan',
  kinds: [],
  prio: 'ch',
  who: '',
  q: '',
  fmax: null,
  camin: null,
  dmode: 'ratt',
  rmode: 'drv',
  smode: 'all',
  trt: 'todo',
};

export const ACTION_LABELS: Record<ActionKind, string> = {
  start: 'Démarrage',
  end: 'Fin de chantier',
  late: 'Solde à facturer',
  bill: 'Sous-facturation',
  drift: 'Dérive de marge',
  rev: 'Révision à jour',
  loss: 'Chantier à perte',
  data: 'Budget manquant',
};

export const DATE_LABELS: Record<DateIssueKind, string> = {
  soon: 'Fin peu crédible',
  pastlow: 'Fin passée, peu avancé',
  nofin: 'Pas de date de fin',
  futstart: 'Démarrage déjà engagé',
  inv: 'Fin avant début',
  old: 'Affaire ancienne',
  futdone: 'Terminé avant la date',
  long: 'Durée anormale',
};

export const CAUSE_LABELS: Record<CostCause, string> = {
  Achats: 'Achats',
  'MO + sous-traitance': 'MO + ST',
  'Autres charges': 'Autres charges',
};

export const VIEW_LABELS: Record<PlanView, string> = {
  plan: 'Actions à mener',
  drift: 'Dérives de marge',
  dates: 'Dates à vérifier',
  sav: 'Gains / pertes achats',
  rec: 'Saving vs écart achats',
};

export const PRIORITY_LABELS = { crit: 'Critique', high: 'Haute', norm: 'Normale' } as const;

const BILL_KINDS: ActionKind[] = ['end', 'late', 'bill'];
const MARGIN_KINDS: ActionKind[] = ['drift', 'loss', 'rev'];

/** Ligne affichable : affaire suivie ou affaire saving seule. */
export type PlanRow = Affaire | SavingOnly;
export const isAffaire = (r: PlanRow): r is Affaire => 'acts' in r;

const inType = (f: PlanFilters, r: { type: string | null }): boolean =>
  !f.ty || (f.ty === '-' ? !r.type : r.type === f.ty);
export const inEntity = (f: PlanFilters, r: { ent: string; type: string | null }): boolean =>
  (f.ent === 'Tous' || r.ent === f.ent) && inType(f, r);
/** Filtres numériques. Les affaires sans taux de facturation (saving seul) restent visibles. */
export const inNumeric = (f: PlanFilters, r: { fa?: number | null; ca: number | null }): boolean =>
  (f.fmax === null || r.fa == null || r.fa <= f.fmax) && (f.camin === null || (r.ca ?? 0) >= f.camin * 1000);
const inAll = (f: PlanFilters, r: PlanRow): boolean => inEntity(f, r) && inNumeric(f, r);

export function savingRows(d: PlanDataset): PlanRow[] {
  return [...d.rows.filter((r) => r.sav), ...d.savx];
}

export const VIEW_PREDICATES: Record<PlanView, (r: PlanRow) => boolean> = {
  plan: (r) => isAffaire(r) && r.acts.length > 0,
  drift: (r) => isAffaire(r) && !!r.drift,
  dates: (r) => isAffaire(r) && r.dates.length > 0,
  sav: (r) => !!r.sav,
  rec: (r) => isAffaire(r) && !!r.sav && !!r.dec,
};

export function baseRows(d: PlanDataset, f: PlanFilters, view: PlanView = f.view): PlanRow[] {
  const src: PlanRow[] = view === 'sav' ? savingRows(d) : d.rows;
  return src.filter((r) => inAll(f, r) && VIEW_PREDICATES[view](r));
}

export function viewCounts(d: PlanDataset, f: PlanFilters): Record<PlanView, number> {
  return {
    plan: baseRows(d, f, 'plan').length,
    drift: baseRows(d, f, 'drift').length,
    dates: baseRows(d, f, 'dates').length,
    sav: baseRows(d, f, 'sav').length,
    rec: baseRows(d, f, 'rec').length,
  };
}

/** Rail des entités : nombre de chantiers critiques à traiter. */
export function entityRail(d: PlanDataset, f: PlanFilters): { code: string; crit: number }[] {
  const codes = ['Tous', ...Object.keys(d.ents).sort()];
  return codes.map((code) => ({
    code,
    crit: d.rows.filter(
      (r) =>
        (code === 'Tous' || r.ent === code) &&
        inType(f, r) &&
        inNumeric(f, r) &&
        r.acts.length &&
        r.prio === 'crit',
    ).length,
  }));
}

/** Répartition par type d'affaire pour l'entité courante. */
export function typeCounts(
  d: PlanDataset,
  f: PlanFilters,
): { all: number; types: { code: string; n: number }[] } {
  const all = [...d.rows, ...d.savx.filter((x) => !d.rows.some((r) => r.code === x.code))].filter(
    (r) => (f.ent === 'Tous' || r.ent === f.ent) && inNumeric(f, r),
  );
  const c: Record<string, number> = {};
  for (const r of all) c[r.type || '-'] = (c[r.type || '-'] ?? 0) + 1;
  const keys = Object.keys(c)
    .filter((k) => k !== '-')
    .sort();
  if (c['-']) keys.push('-');
  return { all: all.length, types: keys.map((code) => ({ code, n: c[code] ?? 0 })) };
}

export interface Rec {
  aP: number;
  aR: number;
  /** Écart achats revu − prévu. */
  e: number;
  /** Saving déclaré. */
  s: number;
  /** Écart non expliqué. */
  x: number;
  /** Achats engagés, en %. */
  eng: number;
  tol: number;
  stt: 'ok' | 'up' | 'dn';
}

export const REC_LABELS = { ok: 'Cohérent', up: 'Surcoût non tracé', dn: 'Baisse non tracée' } as const;

/** Rapprochement saving déclaré / écart achats revu − prévu. */
export function recOf(r: Affaire): Rec {
  const d = r.dec;
  const s = r.sav?.net ?? 0;
  if (!d) return { aP: 0, aR: 0, e: 0, s, x: -s, eng: 0, tol: 1000, stt: 'ok' };
  const e = d.aR - d.aP;
  const x = e - s;
  const eng = d.aR ? Math.round((d.aX / d.aR) * 100) : 0;
  const tol = Math.max(1000, 0.05 * d.aP);
  return { aP: d.aP, aR: d.aR, e, s, x, eng, tol, stt: Math.abs(x) <= tol ? 'ok' : x > 0 ? 'up' : 'dn' };
}

export interface Kpi {
  label: string;
  value: string;
  hint: string;
  /** Mise en avant d'alerte. */
  alert?: boolean;
}

export function planKpis(d: PlanDataset, f: PlanFilters): Kpi[] {
  const R = baseRows(d, f);
  const A = R.filter(isAffaire);
  switch (f.view) {
    case 'plan': {
      let fac = 0;
      let mar = 0;
      for (const r of A)
        for (const a of r.acts) {
          if (BILL_KINDS.includes(a.k)) fac += a.e;
          if (MARGIN_KINDS.includes(a.k)) mar += a.e;
        }
      const n =
        f.ent === 'Tous' ? Object.values(d.ents).reduce((s, e) => s + e.n, 0) : (d.ents[f.ent]?.n ?? 0);
      return [
        { label: 'Chantiers à traiter', value: String(A.length), hint: `sur ${n} affaires actives` },
        {
          label: 'Priorité critique',
          value: String(A.filter((r) => r.prio === 'crit').length),
          hint: 'enjeu ≥ 50 k€ ou à perte',
          alert: true,
        },
        { label: 'À facturer', value: eur(fac), hint: 'soldes, fins, situations' },
        { label: 'Marge en risque', value: eur(mar), hint: 'dérives, pertes, dépassements' },
        {
          label: 'CA concerné',
          value: eur(A.reduce((s, r) => s + r.ca, 0)),
          hint: 'CA prévu des chantiers listés',
        },
      ];
    }
    case 'drift': {
      const t = { A: 0, M: 0, O: 0, d: 0 };
      for (const r of A) {
        if (!r.drift) continue;
        t.A += r.drift.dA;
        t.M += r.drift.dM;
        t.O += r.drift.dO;
        t.d += r.der;
      }
      const ratt = A.filter((r) => r.drift?.ratt);
      const reste = ratt.reduce((s, r) => s + (r.drift ? r.drift.resteA + r.drift.resteM : 0), 0);
      const share = (v: number) => (t.d ? `${Math.round((v / t.d) * 100)} % de la dérive` : '');
      return [
        {
          label: 'Marge perdue',
          value: eur(t.d),
          hint: `${A.length} chantiers en dérive ≥ 3 k€`,
          alert: true,
        },
        { label: 'Due aux achats', value: eur(t.A), hint: share(t.A) },
        { label: 'Due à MO + ST', value: eur(t.M), hint: share(t.M) },
        { label: 'Autres charges', value: eur(t.O), hint: 'frais, aléas, provisions' },
        {
          label: 'Rattrapables',
          value: String(ratt.length),
          hint: `${eur(reste)} de coûts encore à engager`,
        },
      ];
    }
    case 'rec': {
      let e = 0;
      let s = 0;
      let up = 0;
      let dn = 0;
      let nu = 0;
      let nd = 0;
      for (const r of A) {
        const c = recOf(r);
        e += c.e;
        s += c.s;
        if (c.stt === 'up') {
          nu++;
          up += c.x;
        }
        if (c.stt === 'dn') {
          nd++;
          dn += c.x;
        }
      }
      return [
        { label: 'Écart achats revu − prévu', value: eurSigned(e), hint: `${A.length} affaires comparées` },
        { label: 'Saving achats déclaré', value: eurSigned(s), hint: 'somme des line discounts' },
        {
          label: 'Écart non expliqué',
          value: eurSigned(e - s),
          hint: 'écart achats − saving',
          alert: Math.abs(e - s) > 0.1 * Math.abs(e) + 5000,
        },
        {
          label: 'Surcoûts non tracés',
          value: String(nu),
          hint: `${eurSigned(up)} au-dessus du prévu + saving`,
          alert: true,
        },
        { label: 'Baisses non tracées', value: String(nd), hint: `${eurSigned(dn)} sous le prévu + saving` },
      ];
    }
    case 'sav': {
      let g = 0;
      let p = 0;
      let n = 0;
      const bc: Record<string, number> = {};
      for (const r of R) {
        if (!r.sav) continue;
        g += r.sav.gain;
        p += r.sav.perte;
        n += r.sav.n;
        for (const [c, v] of Object.entries(r.sav.by)) bc[c] = (bc[c] ?? 0) + v;
      }
      const bl = Object.entries(bc).sort((a, b) => a[1] - b[1]);
      const tg = bl[0];
      const tp = bl[bl.length - 1];
      return [
        {
          label: 'Résultat net achats',
          value: eurSigned(g + p),
          hint: `${R.length} affaires · ${n} lignes`,
          alert: g + p > 0,
        },
        { label: 'Gains', value: eurSigned(g), hint: 'remises obtenues vs chiffrage' },
        { label: 'Pertes', value: eurSigned(p), hint: 'surcoûts vs chiffrage', alert: true },
        {
          label: '1re source de gain',
          value: tg && tg[1] < 0 ? tg[0] : '—',
          hint: tg && tg[1] < 0 ? eurSigned(tg[1]) : '',
        },
        {
          label: '1re source de perte',
          value: tp && tp[1] > 0 ? tp[0] : '—',
          hint: tp && tp[1] > 0 ? eurSigned(tp[1]) : 'aucune perte nette',
        },
      ];
    }
    case 'dates': {
      const c: Partial<Record<DateIssueKind, number>> = {};
      for (const r of A) for (const x of r.dates) c[x.k] = (c[x.k] ?? 0) + 1;
      const other = (c.futstart ?? 0) + (c.inv ?? 0) + (c.old ?? 0) + (c.futdone ?? 0) + (c.long ?? 0);
      return [
        {
          label: 'Chantiers à dates douteuses',
          value: String(A.length),
          hint: 'au moins une incohérence',
          alert: true,
        },
        {
          label: 'Fin passée, peu avancé',
          value: String(c.pastlow ?? 0),
          hint: 'fin dépassée, coûts < 50 %',
        },
        { label: 'Fin peu crédible', value: String(c.soon ?? 0), hint: 'fin sous 10 j, coûts < 50 %' },
        { label: 'Sans date de fin', value: String(c.nofin ?? 0), hint: 'CA ≥ 20 k€' },
        { label: 'Autres incohérences', value: String(other), hint: 'début engagé, fin avant début…' },
      ];
    }
  }
}

export interface FilterChipDef {
  key: string;
  label: string;
  n: number;
}

/** Puces de filtre secondaires de la vue courante. */
export function kindChips(d: PlanDataset, f: PlanFilters): FilterChipDef[] {
  const R = baseRows(d, f);
  const A = R.filter(isAffaire);
  const cnt: Record<string, number> = {};
  const add = (k: string) => (cnt[k] = (cnt[k] ?? 0) + 1);
  if (f.view === 'plan') {
    for (const r of A) new Set(r.acts.map((a) => a.k)).forEach(add);
    return (Object.keys(ACTION_LABELS) as ActionKind[])
      .filter((k) => cnt[k])
      .map((k) => ({ key: k, label: ACTION_LABELS[k], n: cnt[k] ?? 0 }));
  }
  if (f.view === 'drift') {
    for (const r of A) if (r.drift) add(r.drift.main);
    return (Object.keys(CAUSE_LABELS) as CostCause[])
      .filter((k) => cnt[k])
      .map((k) => ({ key: k, label: `Cause : ${CAUSE_LABELS[k]}`, n: cnt[k] ?? 0 }));
  }
  if (f.view === 'dates') {
    for (const r of A) new Set(r.dates.map((x) => x.k)).forEach(add);
    return (Object.keys(DATE_LABELS) as DateIssueKind[])
      .filter((k) => cnt[k])
      .map((k) => ({ key: k, label: DATE_LABELS[k], n: cnt[k] ?? 0 }));
  }
  if (f.view === 'sav') {
    for (const r of R) if (r.sav) Object.keys(r.sav.by).forEach(add);
    return Object.keys(cnt)
      .sort((a, b) => (cnt[b] ?? 0) - (cnt[a] ?? 0))
      .map((k) => ({ key: k, label: k, n: cnt[k] ?? 0 }));
  }
  return [];
}

/** Compteurs des segments (rattrapables, statuts de rapprochement, gain/perte). */
export function modeCounts(d: PlanDataset, f: PlanFilters): Record<string, number> {
  const R = baseRows(d, f);
  const A = R.filter(isAffaire);
  if (f.view === 'drift')
    return {
      ratt: A.filter((r) => r.drift?.ratt).length,
      past: A.filter((r) => !r.drift?.ratt).length,
      all: A.length,
    };
  if (f.view === 'rec') {
    const c = { ok: 0, up: 0, dn: 0 };
    for (const r of A) c[recOf(r).stt]++;
    return { drv: c.up + c.dn, up: c.up, dn: c.dn, ok: c.ok, all: A.length };
  }
  if (f.view === 'sav')
    return {
      all: R.length,
      perte: R.filter((r) => (r.sav?.net ?? 0) > 0).length,
      gain: R.filter((r) => (r.sav?.net ?? 0) < 0).length,
    };
  return {};
}

/** Responsables (commerciaux et conducteurs) présents dans la vue. */
export function people(d: PlanDataset, f: PlanFilters): string[] {
  const set = new Set<string>();
  for (const r of baseRows(d, f)) {
    if (r.com) set.add(r.com);
    if (r.cond) set.add(r.cond);
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'fr'));
}

const PRIO_ORDER = { crit: 0, high: 1, norm: 2 } as const;

/** Liste filtrée et triée de la vue courante. Les affaires traitées passent en fin de liste. */
export function listRows(d: PlanDataset, f: PlanFilters, done: ReadonlySet<string>): PlanRow[] {
  let R = baseRows(d, f);
  const kinds = new Set(f.kinds);
  const v = f.view;
  if (v === 'plan') {
    R = R.filter(
      (r) =>
        isAffaire(r) && (f.prio === 'all' || (f.prio === 'crit' ? r.prio === 'crit' : r.prio !== 'norm')),
    );
    if (kinds.size) R = R.filter((r) => isAffaire(r) && r.acts.some((a) => kinds.has(a.k)));
  }
  if (v === 'drift') {
    if (f.dmode === 'ratt') R = R.filter((r) => isAffaire(r) && r.drift?.ratt);
    else if (f.dmode === 'past') R = R.filter((r) => isAffaire(r) && !r.drift?.ratt);
    if (kinds.size) R = R.filter((r) => isAffaire(r) && r.drift && kinds.has(r.drift.main));
  }
  if (v === 'dates' && kinds.size) R = R.filter((r) => isAffaire(r) && r.dates.some((x) => kinds.has(x.k)));
  if (v === 'rec')
    R = R.filter((r) => {
      if (!isAffaire(r)) return false;
      const s = recOf(r).stt;
      return f.rmode === 'all' || (f.rmode === 'drv' ? s !== 'ok' : s === f.rmode);
    });
  if (v === 'sav') {
    if (f.smode === 'gain') R = R.filter((r) => (r.sav?.net ?? 0) < 0);
    else if (f.smode === 'perte') R = R.filter((r) => (r.sav?.net ?? 0) > 0);
    if (kinds.size) R = R.filter((r) => r.sav && Object.keys(r.sav.by).some((k) => kinds.has(k)));
  }
  if (f.who) R = R.filter((r) => r.com === f.who || r.cond === f.who);
  if (f.q) {
    const q = f.q.toLowerCase();
    R = R.filter((r) => `${r.nom ?? ''} ${r.cli ?? ''} ${r.code}`.toLowerCase().includes(q));
  }
  const dn = (r: PlanRow) => (done.has(r.code) ? 1 : 0);
  const cmp: Record<PlanView, (a: PlanRow, b: PlanRow) => number> = {
    plan: (a, b) => {
      if (!isAffaire(a) || !isAffaire(b)) return 0;
      return (
        PRIO_ORDER[a.prio] - PRIO_ORDER[b.prio] ||
        (a.due ? 0 : 1) - (b.due ? 0 : 1) ||
        (a.due && b.due ? a.due.localeCompare(b.due) : 0) ||
        b.enj - a.enj
      );
    },
    drift: (a, b) => (isAffaire(a) && isAffaire(b) ? a.der - b.der : 0),
    rec: (a, b) => (isAffaire(a) && isAffaire(b) ? Math.abs(recOf(b).x) - Math.abs(recOf(a).x) : 0),
    sav: (a, b) => (b.sav?.net ?? 0) - (a.sav?.net ?? 0),
    dates: (a, b) => (isAffaire(a) && isAffaire(b) ? b.dates.length - a.dates.length || b.ca - a.ca : 0),
  };
  return [...R].sort((a, b) => dn(a) - dn(b) || cmp[v](a, b));
}

/** Nombre d'affaires à traiter, traitées et au total dans une liste. */
export function traitementCounts(
  rows: readonly PlanRow[],
  done: ReadonlySet<string>,
): Record<TrtFilter, number> {
  const n = rows.filter((r) => done.has(r.code)).length;
  return { todo: rows.length - n, done: n, all: rows.length };
}

/** Garde les affaires à traiter, les traitées, ou toutes ; l'ordre est conservé. */
export function filterByTraitement(rows: PlanRow[], done: ReadonlySet<string>, trt: TrtFilter): PlanRow[] {
  return trt === 'all' ? rows : rows.filter((r) => done.has(r.code) === (trt === 'done'));
}

export function internesFor(
  d: PlanDataset,
  f: PlanFilters,
): { rows: AffaireInterne[]; cost: number; ca: number } {
  const rows = d.internes.filter((r) => inEntity(f, r)).sort((a, b) => b.cr - a.cr);
  return { rows, cost: rows.reduce((s, r) => s + r.cr, 0), ca: rows.reduce((s, r) => s + r.ca, 0) };
}
